import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import Popover from "@material-ui/core/Popover";
import TranslateIcon from "@material-ui/icons/Translate";
import CheckIcon from "@material-ui/icons/Check";

import CloseIcon from "@material-ui/icons/Close";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import LoginBgLight from "../../assets/login-bg-light.png";
import LoginBgDark from "../../assets/login-bg-dark.png";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import FRFlag from "../../assets/france.png";
import clsx from "clsx";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";
import LegalRequirementModal from "../../components/LegalRequirementModal";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Español", icon: ESFlag },
    { value: "fr", label: "Français", icon: FRFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    const { mode } = theme;
    return ({
        // Language Popover Styles
        languagePopover: {
            padding: theme.spacing(2),
            width: 360,
            borderRadius: 16,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
        },
        languageHeader: {
            display: "flex",
            alignItems: "flex-start",
            marginBottom: theme.spacing(2),
            position: "relative",
        },
        languageIconBox: {
            width: 48,
            height: 48,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: theme.spacing(2),
            color: "white",
        },
        languageTitle: {
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#2d3748",
            whiteSpace: "nowrap",
        },
        languageSubtitle: {
            fontSize: "0.85rem",
            color: "#718096",
        },
        closeButton: {
            marginLeft: "auto",
            color: "#a0aec0",
            background: "#EDF2F7",
            borderRadius: "50%",
            padding: 6,
            "&:hover": {
                background: "#E2E8F0",
                color: "#2D3748",
            },
        },
        languageList: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1.5),
        },
        languageItem: {
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(1, 2),
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            background: "white",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                borderColor: "#cbd5e0",
                background: "#f7fafc",
            },
        },
        languageItemActive: {
            border: "2px solid #4299e1",
            background: "rgba(66, 153, 225, 0.1)",
            "&:hover": {
                background: "rgba(66, 153, 225, 0.15)",
            },
        },
        languageItemFlag: {
            width: 30,
            height: 20,
            borderRadius: 4,
            marginRight: theme.spacing(2),
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            border: "1px solid #eee",
            objectFit: "cover",
            display: "block",
            backgroundColor: "#fff",
        },
        languageItemLabel: {
            flex: 1,
            fontWeight: 600,
            color: "#2d3748",
        },
        languageItemCheck: {
            color: "#fff",
            width: 20,
            height: 20,
            background: "#4299e1",
            borderRadius: "50%",
            padding: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        loginPageGlobal: {
            backgroundRepeat: "no-repeat !important",
            backgroundSize: "cover !important",
            backgroundPosition: "center center !important",
            backgroundAttachment: "fixed !important",
            width: "100% !important",
            height: "100% !important",
            minHeight: "100vh !important",
            minWidth: "100vw !important",
            overflow: "hidden !important",
        },
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0",
            margin: "0",
            boxSizing: "border-box",
            overflow: "hidden",
            position: "relative",
            backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
            backgroundImage: "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center center",

            [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
            },
        },

        "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
            "50%": { transform: "translateY(-10px) rotate(180deg)" },
        },

        "@keyframes autofill-curtain": {
            "0%": {
                backgroundSize: "0% 100%",
            },
            "100%": {
                backgroundSize: "100% 100%",
            },
        },

        containerLogin: {
            padding: "16px",
            width: "calc(100% - 32px)",
            zIndex: 10,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            position: "absolute",
            maxWidth: "420px",
            margin: "0 auto",

            [theme.breakpoints.down("sm")]: {
                width: "calc(100% - 40px)",
                maxWidth: "90%",
            },

            [theme.breakpoints.down("xs")]: {
                width: "calc(100% - 32px)",
                maxWidth: "85%",
            },
        },

        paper: {
            backgroundColor: theme.mode === "light" ? "rgba(255, 255, 255, 0.65)" : "rgba(30, 30, 30, 0.65)", // Glass effect
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: theme.spacing(4, 6), // More padding for premium feel
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            maxWidth: "450px",
            width: "100%",
            position: "relative", // Ensure relative for absolute children
            margin: "0 auto",
            [theme.breakpoints.down("xs")]: {
                padding: theme.spacing(2), // Very compact padding
                maxWidth: "65%", // Reducing width to add more margin
                borderRadius: "12px",
                boxShadow: "none", // Remove shadow on mobile to feel lighter? Or keep:
                // boxShadow: "0 4px 16px 0 rgba(31, 38, 135, 0.2)",
            },
        },

        avatar: {
            margin: theme.spacing(1),
            backgroundColor: theme.palette.secondary.main,
        },
        form: {
            width: "100%", // Fix IE 11 issue.
            marginTop: theme.spacing(1),
        },
        submit: {
            margin: theme.spacing(3, 0, 2),
            borderRadius: "12px",
            padding: "12px",
            fontWeight: "bold",
            textTransform: "none",
            fontSize: "1rem",
            fontSize: "1rem",
            boxShadow: theme.mode === "light" ? "0 4px 14px 0 rgba(0,0,0,0.1)" : "none",
            transition: "all 0.2s ease",
            background: mode === "light"
                ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                : `${theme.palette.primary.main}`, // Solid color in dark mode for better visibility
            color: theme.palette.getContrastText(theme.palette.primary.main),
            border: "none",
            "&:hover": {
                transform: "scale(1.02)",
                boxShadow: theme.mode === "light" ? "0 6px 20px rgba(0,0,0,0.15)" : "0 0 15px " + theme.palette.primary.main, // Glow effect
                opacity: 0.9, // ensure visual feedback
            },
        },

        powered: {
            color: "white",
        },

        logoImg: {
            width: "100%",
            maxWidth: "280px",
            height: "auto",
            maxHeight: "80px",
            margin: "0 auto 20px auto",

            [theme.breakpoints.down("xs")]: {
                maxWidth: "150px", // Even smaller logo on mobile
                maxHeight: "50px",
                margin: "0 auto 15px auto",
            },

            content: "url(" + (theme.mode === "light"
                ? theme.calculatedLogoLight()
                : theme.calculatedLogoDark()) + ")",
        },

        iconButton: {
            position: "absolute",
            top: 15,
            right: 15,
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: theme.mode === "light" ? "#374151" : "#F3F4F6", // Adaptive color
            transition: "all 0.3s ease",
            "&:hover": {
                background: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.05)",
            },
        },

        textField: {
            "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fff", // Solid white like languageItem
                transition: "all 0.3s ease",
                "&:hover": {
                    backgroundColor: "#f7fafc", // Hover state like languageItem
                },
                "&.Mui-focused": {
                    backgroundColor: "#fff",
                    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.2)", // Matching blue glow
                },
                "& fieldset": {
                    borderColor: "#e2e8f0", // Subtle grey border like languageItem
                },
                "&:hover fieldset": {
                    borderColor: "#cbd5e0",
                },
                "&.Mui-focused fieldset": {
                    borderColor: "#4299e1", // Matching blue
                    borderWidth: "1px", // Keep it clean
                },
                "& input": {
                    color: "#2D3748", // Ensure text is visible (Dark Grey)
                    fontWeight: 500,
                    paddingLeft: "15px", // Whitespace before text
                },
            },
            "& .MuiInputLabel-root": {
                color: "#718096", // Standard grey text
                fontWeight: 500,
                "&.Mui-focused": {
                    color: "#4299e1",
                },
            },
        },

        languageSelector: {
            position: "absolute",
            top: "15px",
            left: "15px",
            zIndex: 1000,
        },

        registerLink: {
            color: "#3b82f6",
            textDecoration: "none",
            fontWeight: 600,
            transition: "all 0.3s ease",
            "&:hover": {
                color: "#2563eb",
                textDecoration: "underline",
            },
        },

        languageDropdown: {
            display: "flex",
            alignItems: "center",
            background: "none",
            border: "none",
            color: "white",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            gap: "8px",
            transition: "opacity 0.3s ease",
            "&:hover": {
                opacity: 0.8,
            },
        },

        languageOptions: {
            position: "absolute",
            top: "100%",
            left: "0",
            marginTop: "8px",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            padding: "8px",
            zIndex: 1000,
            minWidth: "140px",
        },
        inputLabel: {
            color: theme.mode === "light" ? "#374151" : "#E5E7EB", // Dark text for light mode, Light text for dark mode
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "8px",
            display: "block", // Ensure it takes full width above input
            textAlign: "left",
        },

        inputWrapper: {
            // New styles for the input container to look like the image
            "& .MuiOutlinedInput-root": {
                borderRadius: "16px", // Rounded corners
                backgroundColor: theme.mode === "light" ? "#F3F4F6" : "#2a2a2a", // Adaptive background
                transition: "all 0.3s ease",
                paddingLeft: "12px", // Increased padding for icon separation
                "& fieldset": {
                    borderColor: "transparent", // No border by default
                },
                "&:hover fieldset": {
                    borderColor: "transparent",
                },
                "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main, // Use Primary Color from Settings
                    borderWidth: "2px",
                },
                "& input": {
                    color: theme.mode === "light" ? "#1F2937" : "#F3F4F6", // Adaptive text color
                    fontWeight: 500,
                    fontSize: "16px !important", // FORCE 16px to override browser agent styles
                    fontFamily: "'Inter', sans-serif !important", // FORCE font family
                    padding: "16px 14px 16px 0", // Adjust padding for icon alignment
                },
                // "Curtain" animation for autofill
                // Moved to root level to fix JSS warning
                "& input:-webkit-autofill": {
                    "-webkit-box-shadow": "0 0 0 100px " + (theme.palette.type === "light" ? "#F3F4F6" : "#2a2a2a") + " inset", // Background fix
                    "-webkit-text-fill-color": theme.palette.type === "light" ? "#1F2937" : "#F3F4F6",
                    transition: "background-color 5000s ease-in-out 0s", // Delay native background
                    caretColor: theme.palette.primary.main,
                    // Curtain effect hack using background-image on parent or shadow?
                    // Actually, modifying shadow is the robust way:
                    boxShadow: "inset 0 0 0 100px " + theme.palette.primary.main + "20", // 20% opacity primary
                    animation: "$autofill-curtain 1s ease-out forwards",
                    // Note: animation on autofill pseudo is limited. 
                    // We stick to the color override which is the main user request ("clarito").
                    // We stick to the color override which is the main user request ("clarito").
                    fontSize: "16px !important", // Force font size to prevent resizing
                    fontFamily: "'Inter', sans-serif !important", // seamless font match
                },
                "& input:-webkit-autofill:hover": {
                    "-webkit-box-shadow": "0 0 0 100px " + (theme.palette.type === "light" ? "#F3F4F6" : "#2a2a2a") + " inset, 0 0 0 100px " + theme.palette.primary.main + "20 inset",
                },
                "& input:-webkit-autofill:focus": {
                    "-webkit-box-shadow": "0 0 0 100px " + (theme.palette.type === "light" ? "#F3F4F6" : "#2a2a2a") + " inset, 0 0 0 100px " + theme.palette.primary.main + "33 inset", // Slightly darker on focus
                },
                [theme.breakpoints.down("xs")]: {
                    borderRadius: "12px",
                    paddingLeft: "8px",
                    "& input": {
                        padding: "12px 10px 12px 0", // More compact input on mobile
                        fontSize: "0.9rem",
                    }
                },
            },
        },

        forgotPassLink: {
            color: theme.mode === "light" ? "#6B7280" : "#9CA3AF", // Adjustable grey
            fontSize: "12px",
            fontWeight: 500,
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
                color: theme.mode === "light" ? "#374151" : "#F3F4F6",
            },
        },

        formControl: {
            marginBottom: theme.spacing(3),
            width: "100%",
        },
        languageOption: {
            background: "none",
            border: "none",
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "8px 12px",
            textAlign: "left",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                background: "rgba(59, 130, 246, 0.1)",
                color: "#3b82f6",
            },
        },

        flagIcon: {
            width: 20,
            height: 15,
            borderRadius: 2,
        },

        versionInfo: {
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
            background: "rgba(0, 0, 0, 0.6)", // Darker for contrast
            backdropFilter: "blur(10px)",
            borderRadius: "8px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "6px 12px",
            color: "white",
            fontSize: "12px",
            fontWeight: 500,
            opacity: 0.8,
            transition: "opacity 0.3s ease",
            "&:hover": {
                opacity: 1,
            },
        },
    });
});

const Login = () => {
    const classes = useStyles();
    const mounted = useRef(true);
    const theme = useTheme();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();

    const { handleLogin } = useContext(AuthContext);
    const [rememberMe, setRememberMe] = useState(false); // New state for checkbox

    useEffect(() => {
        const savedEmail = localStorage.getItem("email");
        if (savedEmail) {
            setUser((prev) => ({ ...prev, email: savedEmail }));
            setRememberMe(true);
        }
    }, []);

    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const [, setForceUpdate] = useState(Date.now());

    useEffect(() => {
        const handleLanguageChanged = () => {
            setForceUpdate(Date.now());
        };
        i18n.on("languageChanged", handleLanguageChanged);
        return () => {
            i18n.off("languageChanged", handleLanguageChanged);
        };
    }, []);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [resendEmail, setResendEmail] = useState("");
    const [resendLoading, setResendLoading] = useState(false);

    const handleResendActivation = async () => {
        if (!resendEmail || !resendEmail.trim()) {
            toast.error("El correo electrónico es obligatorio.");
            return;
        }
        setResendLoading(true);
        try {
            const { data } = await api.post("/auth/resend-verification", { email: resendEmail });
            toast.success(data?.message || "Se ha enviado un nuevo enlace de activación a tu correo.");
            setResendModalOpen(false);
        } catch (err) {
            toastError(err);
        } finally {
            setResendLoading(false);
        }
    };

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        localStorage.setItem("i18nextLng", language);
    };

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        if (rememberMe) {
            localStorage.setItem("email", user.email);
        } else {
            localStorage.removeItem("email");
        }
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                if (mounted.current) {
                    setAllowSignup(data === "enabled");
                }
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                if (mounted.current) {
                    let arr = ["pt-BR", "en"];
                    try {
                        if (langs) arr = JSON.parse(langs);
                    } catch { }
                    setEnabledLanguages(arr);
                }
            })
            .catch(() => {
                if (mounted.current) {
                    setEnabledLanguages(["pt-BR", "en"]);
                }
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (mounted.current) {
                    if (bgLight) {
                        const backendUrl = getBackendUrl();
                        const fullUrl = backendUrl + "/public/" + bgLight;
                        // console.log("Background light URL:", fullUrl);
                        setBackgroundLight(fullUrl);
                    } else {
                        setBackgroundLight("");
                    }
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                if (mounted.current) {
                    setBackgroundLight("");
                }
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (mounted.current) {
                    if (bgDark) {
                        const backendUrl = getBackendUrl();
                        const fullUrl = backendUrl + "/public/" + bgDark;
                        // console.log("Background dark URL:", fullUrl);
                        setBackgroundDark(fullUrl);
                    } else {
                        setBackgroundDark("");
                    }
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                if (mounted.current) {
                    setBackgroundDark("");
                }
            });

        return () => {
            mounted.current = false;
        };
    }, []);



    let finalBackground;

    // console.log("Modo:", mode);
    // console.log("Background Light URL:", backgroundLight);
    // console.log("Background Dark URL:", backgroundDark);

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
            // console.log("Usando background light:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
            // console.log("Usando cor do tema light:", finalBackground);
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
            // console.log("Usando background dark:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
            // console.log("Usando cor do tema dark:", finalBackground);
        }
    }

    // console.log("Background final definido:", finalBackground);


    return (
        <>
            <Helmet>
                <title>{appName || i18n.t("login.title")}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div
                className={classes.root}
                style={{
                    backgroundImage: mode === "light"
                        ? `url(${LoginBgLight})`
                        : `url(${LoginBgDark})`,
                }}
            >


                {/* Removed Container wrapper to allow direct flex centering from root */}
                {/* Language Selector Top Left */}
                <div
                    className={classes.languageSelector}
                >
                    <button
                        onClick={(e) => setLangAnchorEl(e.currentTarget)}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            paddingRight: "20px",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {(() => {
                            const currentLang = languageOptions.find(l => i18n.language.startsWith(l.value) || l.value.startsWith(i18n.language)) || languageOptions[0];
                            return (
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                    border: mode === "light" ? "2px solid #fff" : "2px solid rgba(255,255,255,0.2)",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "4px",
                                    background: mode === "light" ? "white" : "rgba(0,0,0,0.4)", // Adaptive background
                                    backdropFilter: "blur(4px)",
                                }}>
                                    <img
                                        src={currentLang.icon}
                                        alt={currentLang.label}
                                        style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                                    />
                                </div>
                            );
                        })()}
                    </button>

                    <Popover
                        id="language-popover"
                        open={Boolean(langAnchorEl)}
                        anchorEl={langAnchorEl}
                        onClose={() => setLangAnchorEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        classes={{ paper: classes.languagePopover }}
                    >
                        <div className={classes.languageHeader}>
                            <div className={classes.languageIconBox}>
                                <TranslateIcon style={{ fontSize: 24 }} />
                            </div>
                            <div style={{ marginRight: 16 }}>
                                <Typography className={classes.languageTitle}>
                                    {i18n.t("layout.languageModal.title")}
                                </Typography>
                                <Typography className={classes.languageSubtitle}>
                                    {i18n.t("layout.languageModal.subtitle")}
                                </Typography>
                            </div>
                            <IconButton
                                className={classes.closeButton}
                                onClick={() => setLangAnchorEl(null)}
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        <div className={classes.languageList}>
                            {languageOptions
                                .filter((opt) => enabledLanguages.includes(opt.value))
                                .map((lang) => {
                                    const isActive = i18n.language === lang.value;
                                    return (
                                        <div
                                            key={lang.value}
                                            className={clsx(classes.languageItem, {
                                                [classes.languageItemActive]: isActive,
                                            })}
                                            onClick={() => {
                                                handleLanguageChange(lang.value);
                                                setLangAnchorEl(null);
                                            }}
                                        >
                                            <img
                                                src={lang.icon}
                                                alt={lang.label}
                                                className={classes.languageItemFlag}
                                            />
                                            <Typography className={classes.languageItemLabel}>
                                                {lang.value === "pt-BR" ? "Português" : lang.value === "es" ? "Español" : lang.value === "en" ? "English" : lang.label}
                                            </Typography>
                                            {isActive && (
                                                <div className={classes.languageItemCheck}>
                                                    <CheckIcon style={{ fontSize: 16, color: "#fff" }} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </Popover>
                </div>

                {/* Dark Mode Toggle Removed to enforce Dark Mode */}

                <div className={classes.paper}>


                    <div>
                        <img className={classes.logoImg} alt="logo" />
                    </div>

                    <form className={classes.form} noValidate onSubmit={handlSubmit}>
                        <div className={classes.formControl}>
                            <Typography className={classes.inputLabel}>
                                {i18n.t("login.form.email")}
                            </Typography>
                            <TextField
                                variant="outlined"
                                margin="none" // Controlled by wrapper
                                required
                                fullWidth
                                id="email"
                                name="email"
                                value={user.email}
                                onChange={handleChangeInput}
                                autoComplete="email"
                                autoFocus
                                className={classes.inputWrapper}
                                placeholder="example@email.com" // Placeholder instead of label
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon style={{ color: theme.palette.primary.main }} />
                                            {/* Spacer to align text if needed, or just rely on padding */}
                                            <div style={{ width: "8px" }}></div>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>

                        <div className={classes.formControl}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <Typography className={classes.inputLabel} style={{ marginBottom: 0 }}>
                                    {i18n.t("login.form.password")}
                                </Typography>
                                <Link
                                    component={RouterLink}
                                    to="/forget-password" // Assuming this route exists or is standard
                                    className={classes.forgotPassLink}
                                >
                                    {i18n.t("login.form.forgot_password") || "Olvidé mi Contraseña"} {/* Fallback string if translation missing */}
                                </Link>
                            </div>
                            <TextField
                                variant="outlined"
                                margin="none"
                                required
                                fullWidth
                                name="password"
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={user.password}
                                onChange={handleChangeInput}
                                autoComplete="current-password"
                                className={classes.inputWrapper}
                                placeholder="••••••••"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon style={{ color: theme.palette.primary.main }} />
                                            <div style={{ width: "8px" }}></div>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                style={{ color: "#6b7280" }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>

                        {/* Remember Me Checkbox */}
                        <FormControlLabel
                            control={<Checkbox
                                value="remember"
                                color="primary"
                                size="small"
                                style={{ padding: '6px' }}
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />}
                            label={<span style={{ fontSize: "0.85rem" }}>{i18n.t("login.form.remember_me") || "Recordar Credenciales"}</span>}
                            style={{ width: "100%", textAlign: "left", color: mode === "light" ? "#4B5563" : "#D1D5DB", marginBottom: "16px" }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            startIcon={<ExitToAppIcon />} // Added Login Icon
                        >
                            {i18n.t("login.buttons.submit")}
                        </Button>

                        {/* Always show Register Link as requested, assuming allowSignup might be false/undefined but user wants it visible */}
                        <div style={{ marginTop: "24px", textAlign: "center" }}>
                            <Link
                                component={RouterLink}
                                to="/signup"
                                className={classes.registerLink}
                                style={{ fontSize: "0.95rem" }}
                            >
                                {i18n.t("login.buttons.register") || "¿No tienes cuenta? ¡Regístrate!"}
                            </Link>

                            <div style={{ marginTop: "14px" }}>
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setResendEmail(user.email || "");
                                        setResendModalOpen(true);
                                    }}
                                    style={{
                                        color: mode === "light" ? "#0056b3" : "#90caf9",
                                        fontSize: "0.85rem",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                        cursor: "pointer"
                                    }}
                                >
                                    ¿No recibiste el correo de activación? Reenviar enlace
                                </Link>
                            </div>

                            <Grid container justifyContent="center" spacing={2} style={{ marginTop: "15px" }}>
                                <Grid item>
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={() => setShowTerms(true)}
                                        style={{ color: mode === "light" ? "#6b7280" : "#9ca3af", fontSize: "0.8rem", textDecoration: "none" }}
                                    >
                                        {i18n.t("signup.links.terms")}
                                    </Link>
                                </Grid>
                                <Grid item>
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={() => setShowPrivacy(true)}
                                        style={{ color: mode === "light" ? "#6b7280" : "#9ca3af", fontSize: "0.8rem", textDecoration: "none" }}
                                    >
                                        {i18n.t("signup.links.privacy")}
                                    </Link>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                </div>


                {/* Informação da versão */}
                <Typography className={classes.versionInfo}>
                    Botmix Ver: {packageJson.version}
                </Typography>

                <LegalRequirementModal
                    open={showTerms}
                    onClose={() => setShowTerms(false)}
                    type="terms"
                />
                <LegalRequirementModal
                    open={showPrivacy}
                    onClose={() => setShowPrivacy(false)}
                    type="privacy"
                />

                {/* Modal de Reenvío de Enlace de Activación */}
                <Dialog
                    open={resendModalOpen}
                    onClose={() => !resendLoading && setResendModalOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: 16,
                            padding: "8px"
                        }
                    }}
                >
                    <DialogTitle style={{ fontWeight: "bold" }}>
                        Reenviar Enlace de Activación
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, lineHeight: 1.5 }}>
                            Ingresa el correo electrónico con el que te registraste. Te enviaremos un nuevo enlace de activación válido por <strong>7 días</strong>.
                        </Typography>
                        <TextField
                            autoFocus
                            variant="outlined"
                            fullWidth
                            label="Correo Electrónico"
                            type="email"
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                            disabled={resendLoading}
                        />
                    </DialogContent>
                    <DialogActions style={{ padding: "16px 24px" }}>
                        <Button
                            onClick={() => setResendModalOpen(false)}
                            color="default"
                            disabled={resendLoading}
                            style={{ textTransform: "none" }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleResendActivation}
                            color="primary"
                            variant="contained"
                            disabled={resendLoading}
                            style={{ textTransform: "none", fontWeight: "bold", minWidth: 100 }}
                        >
                            {resendLoading ? <CircularProgress size={22} color="inherit" /> : "Reenviar"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div >
        </>
    );
};

export default Login;
